import {
  RawSocketMessage,
  SocketMessageType,
  AuthorizationData,
  SocketAuthorizeTokenPayload,
} from '../types/socket.types';
import { AUTHORIZATION_EVENTS } from '../constants/socket.constants';

export class MessageParser {
  /**
   * Extract actual data from nested array structure
   */
  private static extractActualData(data: SocketAuthorizeTokenPayload): RawSocketMessage | null {
    if (!data) return null;

    // Handle array format [eventName, data]
    if (Array.isArray(data) && data.length >= 2) {
      return data[1];
    }

    return data;
  }

  /**
   * Check if data represents a session acceptance event
   */
  static isSessionAcceptedEvent(data: RawSocketMessage): boolean {
    return data.type === SocketMessageType.SESSION_ACCEPTED;
  }

  /**
   * Check if data represents a session end event
   */
  static isSessionEndEvent(data: RawSocketMessage): boolean {
    return data.type === SocketMessageType.SESSION_ENDED;
  }

  /**
   * Check if data represents an authorization event with access token
   */
  static isAuthorizationEvent(data: RawSocketMessage): boolean {
    return (
      data.type === SocketMessageType.AUTHORIZATION_TOKEN ||
      data.type === SocketMessageType.ACCESS_TOKEN_RECEIVED ||
      data.type === AUTHORIZATION_EVENTS.TOKEN_RECEIVED ||
      data.type === AUTHORIZATION_EVENTS.ACCESS_GRANTED ||
      data.type === AUTHORIZATION_EVENTS.SESSION_AUTHORIZED ||
      (data.accessToken !== undefined && data.accessToken !== null) ||
      data.authorization !== undefined ||
      data.data?.accessToken !== undefined
    );
  }

  /**
   * Check if data represents a denied authorization event
   */
  static isDeniedAuthorizationEvent(data: RawSocketMessage): boolean {
    return data.status === 'expired';
  }

  /**
   * Extract authorization data from socket message
   */
  static extractAuthorizationData(data: RawSocketMessage): AuthorizationData | null {
    if (!this.isAuthorizationEvent(data)) {
      return null;
    }

    // Try different locations for access token and authorization data
    let accessToken: string | undefined;
    let refreshToken: string | undefined;
    let authData: Partial<AuthorizationData> = {};

    // Check direct accessToken field
    if (data.accessToken) {
      accessToken = data.accessToken;
    }

    if (data.refreshToken) {
      refreshToken = data.refreshToken;
    }

    // Check authorization object
    if (data.authorization) {
      accessToken = accessToken || data.authorization.accessToken;
      refreshToken = refreshToken || data.authorization.refreshToken;
      authData = { ...data.authorization };
    }

    // Check data object
    if (data.data) {
      accessToken = accessToken || data.data.accessToken;
      refreshToken = refreshToken || data.data.refreshToken;
      authData = { ...authData, ...data.data };
    }

    // Check nested message object
    if (data.message && typeof data.message === 'object') {
      const messageData = data.message as Record<string, unknown>;
      const messageAccessToken =
        typeof messageData.accessToken === 'string' ? messageData.accessToken : undefined;
      accessToken = accessToken || messageAccessToken;
      const messageRefreshToken =
        typeof messageData.refreshToken === 'string' ? messageData.refreshToken : undefined;
      refreshToken = refreshToken || messageRefreshToken;
      if (messageData.authorization) {
        authData = { ...authData, ...messageData.authorization };
      }
    }

    if (!accessToken || !refreshToken) {
      return null;
    }

    return {
      accessToken,
      refreshToken,
      sessionId: authData.sessionId || data.sessionId || '',
      userId: authData.userId || data.userId,
      expiresAt: authData.expiresAt,
      permissions: authData.permissions,
      status: authData.status || '',
    };
  }

  /**
   * Extract access token from raw socket data (convenience method)
   */
  static extractAccessToken(data: RawSocketMessage): string | null {
    const authData = this.extractAuthorizationData(data);
    return authData?.accessToken || null;
  }

  /**
   * Process socket data and extract actual data from nested structure
   */
  static processSocketData(data: SocketAuthorizeTokenPayload): RawSocketMessage | null {
    return this.extractActualData(data);
  }
}
